/**
 * 🎡 MEGA FORTUNE WHEEL - Roue de la Fortune Ultra Réaliste
 * Rotation physique avec décélération progressive et sons de cliquetis
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { GameSounds } from './GameSounds';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Segments de la roue avec couleurs vibrantes
const WHEEL_SEGMENTS = [
  { label: '🏆 TAMPON', color: '#FFD700', textColor: '#000', prizeKey: 'stamp' },
  { label: '5 pts', color: '#6366F1', textColor: '#FFF', prizeKey: 'points5' },
  { label: '-5%', color: '#EF4444', textColor: '#FFF', prizeKey: 'discount5' },
  { label: '5 pts', color: '#8B5CF6', textColor: '#FFF', prizeKey: 'points5' },
  { label: '🍀 REJOUE', color: '#22C55E', textColor: '#FFF', prizeKey: 'replay' },
  { label: '10 pts', color: '#3B82F6', textColor: '#FFF', prizeKey: 'points10' },
  { label: '5 pts', color: '#EC4899', textColor: '#FFF', prizeKey: 'points5' },
  { label: '25 pts', color: '#F59E0B', textColor: '#000', prizeKey: 'points25' },
  { label: '5 pts', color: '#14B8A6', textColor: '#FFF', prizeKey: 'points5' },
  { label: '-10%', color: '#DC2626', textColor: '#FFF', prizeKey: 'discount10' },
  { label: '5 pts', color: '#7C3AED', textColor: '#FFF', prizeKey: 'points5' },
  { label: '50 pts', color: '#F97316', textColor: '#FFF', prizeKey: 'points50' },
];

// Configuration des probabilités
const PRIZE_CONFIG = {
  stamp: { probability: 0.001, label: '🏆 TAMPON!', value: 'stamp', points: 0 },
  discount10: { probability: 0.001, label: '🎁 -10%', value: 'discount10', points: 0 },
  discount5: { probability: 0.003, label: '🎁 -5%', value: 'discount5', points: 0 },
  replay: { probability: 0.03, label: '🍀 Rejoue!', value: 'replay', points: 0 },
  points50: { probability: 0.005, label: '⭐ 50 pts', value: '50', points: 50 },
  points25: { probability: 0.02, label: '⭐ 25 pts', value: '25', points: 25 },
  points10: { probability: 0.10, label: '⭐ 10 pts', value: '10', points: 10 },
  points5: { probability: 0.84, label: '✨ 5 pts', value: '5', points: 5 },
};

const determineWinning = () => {
  const random = Math.random();
  let cumulative = 0;
  for (const [key, config] of Object.entries(PRIZE_CONFIG)) {
    cumulative += config.probability;
    if (random <= cumulative) return { key, ...config };
  }
  return { key: 'points5', ...PRIZE_CONFIG.points5 };
};

// Composant SVG de la roue
const WheelSVG = ({ rotation, segments }) => {
  const segmentAngle = 360 / segments.length;
  
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-2xl">
      <defs>
        {/* Filtres pour les effets */}
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        {/* Gradient pour le centre */}
        <radialGradient id="centerGradient">
          <stop offset="0%" stopColor="#FFD700"/>
          <stop offset="100%" stopColor="#B8860B"/>
        </radialGradient>
      </defs>
      
      {/* Cercle extérieur décoratif */}
      <circle cx="100" cy="100" r="98" fill="none" stroke="#FFD700" strokeWidth="4" filter="url(#glow)"/>
      
      {/* Roue principale */}
      <g transform={`rotate(${rotation} 100 100)`}>
        {segments.map((segment, i) => {
          const startAngle = i * segmentAngle - 90;
          const endAngle = (i + 1) * segmentAngle - 90;
          
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          
          const x1 = 100 + 90 * Math.cos(startRad);
          const y1 = 100 + 90 * Math.sin(startRad);
          const x2 = 100 + 90 * Math.cos(endRad);
          const y2 = 100 + 90 * Math.sin(endRad);
          
          const largeArc = segmentAngle > 180 ? 1 : 0;
          
          // Position du texte (au milieu du segment)
          const midAngle = ((startAngle + endAngle) / 2 * Math.PI) / 180;
          const textX = 100 + 60 * Math.cos(midAngle);
          const textY = 100 + 60 * Math.sin(midAngle);
          const textRotation = (startAngle + endAngle) / 2 + 90;
          
          return (
            <g key={i}>
              <path
                d={`M 100 100 L ${x1} ${y1} A 90 90 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={segment.color}
                stroke="#FFF"
                strokeWidth="1"
              />
              <text
                x={textX}
                y={textY}
                fill={segment.textColor}
                fontSize="8"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${textRotation} ${textX} ${textY})`}
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {segment.label}
              </text>
            </g>
          );
        })}
        
        {/* Pins/clous autour de la roue */}
        {segments.map((_, i) => {
          const angle = (i * segmentAngle - 90) * Math.PI / 180;
          const x = 100 + 88 * Math.cos(angle);
          const y = 100 + 88 * Math.sin(angle);
          return (
            <circle
              key={`pin-${i}`}
              cx={x}
              cy={y}
              r="3"
              fill="#FFD700"
              stroke="#B8860B"
              strokeWidth="1"
            />
          );
        })}
      </g>
      
      {/* Centre de la roue */}
      <circle cx="100" cy="100" r="20" fill="url(#centerGradient)" stroke="#FFF" strokeWidth="2"/>
      <text x="100" y="100" fontSize="16" textAnchor="middle" dominantBaseline="middle">🎰</text>
    </svg>
  );
};

// Confettis de victoire
const Confetti = ({ active }) => {
  if (!active) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
      {[...Array(40)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 sm:w-3 sm:h-3"
          initial={{
            x: '50%',
            y: '30%',
            scale: 0,
            rotate: 0,
          }}
          animate={{
            x: `${Math.random() * 100}%`,
            y: `${Math.random() * 100 + 50}%`,
            scale: [0, 1, 1, 0],
            rotate: Math.random() * 720,
          }}
          transition={{
            duration: 2.5,
            delay: i * 0.03,
            ease: 'easeOut',
          }}
          style={{
            backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#22C55E', '#EC4899'][i % 6],
            borderRadius: Math.random() > 0.5 ? '50%' : '0%',
          }}
        />
      ))}
    </div>
  );
};

export function MegaFortuneWheel({ customerId, onWin, gameStatus, isTraining, onGameEnd }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const tickIntervalRef = useRef(null);
  const currentRotationRef = useRef(0);

  const spinWheel = async () => {
    if (isSpinning) return;
    
    // Initialiser les sons
    GameSounds.init();
    GameSounds.wheelStart(); // Son d'accélération de la roue !
    GameSounds.click();
    
    setIsSpinning(true);
    setShowResult(false);
    setResult(null);
    setShowConfetti(false);

    // Déterminer le résultat
    const winning = determineWinning();
    
    // Trouver le segment correspondant
    const segmentIndex = WHEEL_SEGMENTS.findIndex(s => s.prizeKey === winning.key);
    const segmentAngle = 360 / WHEEL_SEGMENTS.length;
    const targetAngle = 360 - (segmentIndex * segmentAngle) - (segmentAngle / 2);
    
    // Nombre de tours + angle final
    const spins = 5 + Math.floor(Math.random() * 3);
    const finalRotation = rotation + (spins * 360) + targetAngle;
    
    // Animation avec sons de cliquetis
    const startTime = Date.now();
    const duration = 6000; // 6 secondes
    const startRotation = rotation;
    const totalRotation = finalRotation - startRotation;
    
    // Cliquetis pendant la rotation
    let lastTickAngle = 0;
    const tickAngle = 360 / WHEEL_SEGMENTS.length;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing: décélération exponentielle
      const easeOut = 1 - Math.pow(1 - progress, 4);
      const currentRotation = startRotation + totalRotation * easeOut;
      
      setRotation(currentRotation);
      currentRotationRef.current = currentRotation;
      
      // Jouer le cliquetis quand on passe un segment
      const normalizedRotation = currentRotation % 360;
      const currentTickAngle = Math.floor(normalizedRotation / tickAngle) * tickAngle;
      
      if (currentTickAngle !== lastTickAngle) {
        const speed = 1 - progress; // Plus lent = son plus grave
        GameSounds.tick(0.8 + speed * 0.4);
        lastTickAngle = currentTickAngle;
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Fin de rotation
        handleSpinEnd(winning);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const handleSpinEnd = async (winning) => {
    setIsSpinning(false);
    setResult(winning);
    setShowResult(true);
    
    // Sons et effets
    if (winning.key === 'stamp' || winning.key === 'discount10') {
      GameSounds.bigWinFanfare(); // Fanfare améliorée !
      setShowConfetti(true);
    } else if (winning.key === 'replay') {
      GameSounds.replay(); // Son de replay !
    } else if (winning.key.startsWith('discount')) {
      GameSounds.giftSound(); // Son de cadeau !
    } else if (winning.points >= 25) {
      GameSounds.win();
    } else {
      GameSounds.coins();
    }
    
    // Enregistrer la partie
    try {
      const prizeType = winning.points > 0 ? 'points' : 
                       winning.key === 'replay' ? 'replay' :
                       winning.key === 'stamp' ? 'stamp' :
                       winning.key.startsWith('discount') ? 'discount' : 'nothing';
      
      const prizeValue = winning.points > 0 ? winning.value : 
                        winning.key.startsWith('discount') ? winning.key : winning.value;
      
      const response = await api.recordGamePlay(
        customerId,
        'wheel',
        prizeType,
        prizeValue,
        null,
        isTraining
      );
      
      if (response.message) {
        toast.success(response.message);
      }
      
      if (onWin) onWin(winning);
      if (onGameEnd) onGameEnd();
    } catch (error) {
      console.error('Erreur enregistrement:', error);
    }
  };

  return (
    <div className="relative flex flex-col items-center py-4">
      {/* Confettis */}
      <Confetti active={showConfetti} />
      
      {/* Mode entraînement */}
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      {/* Titre */}
      <h3 className="text-2xl sm:text-3xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
        🎡 ROUE DE LA FORTUNE 🎡
      </h3>
      
      {/* Roue */}
      <div className="relative w-64 h-64 sm:w-80 sm:h-80 mb-6">
        {/* Indicateur/flèche */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 z-20">
          <motion.div 
            className="relative"
            animate={isSpinning ? { y: [0, 3, 0] } : {}}
            transition={{ duration: 0.1, repeat: isSpinning ? Infinity : 0 }}
          >
            <div className="w-0 h-0 border-l-[18px] border-r-[18px] border-t-[30px] border-l-transparent border-r-transparent border-t-red-600 drop-shadow-lg" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-red-400" />
          </motion.div>
        </div>
        
        {/* Lumières autour de la roue */}
        <div className="absolute inset-0 rounded-full">
          {[...Array(16)].map((_, i) => {
            const angle = (i * 22.5 - 90) * Math.PI / 180;
            const x = 50 + 52 * Math.cos(angle);
            const y = 50 + 52 * Math.sin(angle);
            return (
              <motion.div
                key={i}
                className="absolute w-3 h-3 rounded-full"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{
                  backgroundColor: isSpinning 
                    ? ['#FFD700', '#FF0000', '#00FF00', '#FF00FF', '#FFD700']
                    : i % 2 === 0 ? '#FFD700' : '#FF4500',
                  boxShadow: isSpinning 
                    ? ['0 0 10px #FFD700', '0 0 10px #FF0000', '0 0 10px #00FF00']
                    : '0 0 8px #FFD700',
                }}
                transition={{
                  duration: 0.2,
                  repeat: isSpinning ? Infinity : 0,
                  delay: i * 0.05,
                }}
              />
            );
          })}
        </div>
        
        {/* La roue SVG */}
        <div className="absolute inset-4">
          <WheelSVG rotation={rotation} segments={WHEEL_SEGMENTS} />
        </div>
      </div>

      {/* Bouton SPIN */}
      <motion.button
        onClick={spinWheel}
        disabled={isSpinning}
        className="px-10 py-4 text-xl font-black rounded-full text-white shadow-lg"
        style={{
          background: isSpinning 
            ? 'linear-gradient(135deg, #666 0%, #444 100%)' 
            : 'linear-gradient(135deg, #FFD700 0%, #FF8C00 50%, #FF4500 100%)',
          boxShadow: isSpinning ? 'none' : '0 6px 0 #B8860B, 0 8px 25px rgba(255,140,0,0.4)',
        }}
        whileHover={!isSpinning ? { scale: 1.05 } : {}}
        whileTap={!isSpinning ? { scale: 0.98, y: 4 } : {}}
      >
        {isSpinning ? (
          <span className="flex items-center gap-2">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 0.5, repeat: Infinity, ease: 'linear' }}
            >
              🎡
            </motion.span>
            ÇA TOURNE...
          </span>
        ) : (
          <span>🎯 TOURNER !</span>
        )}
      </motion.button>

      {/* Résultat */}
      <AnimatePresence>
        {showResult && result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`mt-6 p-6 rounded-2xl text-center ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-2 border-yellow-400 shadow-[0_0_30px_rgba(255,215,0,0.3)]'
                : result.points >= 25 || result.key === 'replay'
                ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <motion.span 
              className="text-5xl block mb-2"
              animate={result.key === 'stamp' ? { 
                scale: [1, 1.3, 1],
                rotate: [0, 10, -10, 0]
              } : {}}
              transition={{ duration: 0.5, repeat: result.key === 'stamp' ? Infinity : 0 }}
            >
              {result.key === 'stamp' ? '🏆' : 
               result.key === 'replay' ? '🍀' :
               result.key.startsWith('discount') ? '🎁' : '⭐'}
            </motion.span>
            <span className="text-2xl font-black text-white">{result.label}</span>
            {isTraining && <p className="text-xs text-gray-400 mt-2">(Mode entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MegaFortuneWheel;
